/**
 * Activities Step
 *
 * Personal sport and activity preferences selection.
 * Issue #563 - First-Run Onboarding Flow
 * Issue #721 - Enhanced with Nightfall Figma design patterns
 */

import { useState } from 'react';
import { Heart, ChevronLeft, ArrowRight } from 'lucide-react';
import { OroLoader } from '../../OroLoader';
import { profileApi } from '@/lib/api/profile';
import { ApiError } from '@/lib/api';
import { PROFILE_SPORTS, MAX_PROFILE_SPORTS } from '@/data/sports';

interface ActivitiesStepProps {
  onNext: () => void;
  onBack: () => void;
}

// Sport chips come from the canonical shared list (frontend/src/data/sports.ts).
// Every id there is schema-legal (a member of backend ALLOWED_SPORTS) — pinned
// by backend/tests/integration/sports-drift.test.ts. Do NOT hand-copy sports
// here (#2698: a hand-copied 12-list offered Mountain Biking/Triathlon/Other,
// none of which ALLOWED_SPORTS accepted, so PUT /profile 400'd the whole update).
const SPORTS = PROFILE_SPORTS;

export default function ActivitiesStep({ onNext, onBack }: ActivitiesStepProps) {
  const [selectedSports, setSelectedSports] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cap total selection at 1 primary + 5 secondary = MAX_PROFILE_SPORTS (#2698).
  // Mirrors UpdateProfileSchema (primary_sport + secondary_sports.max(5)); a 7th
  // selection would 400 the whole update, so block it at the UI instead.
  const atSelectionCap = selectedSports.length >= MAX_PROFILE_SPORTS;

  const toggleSport = (sportId: string) => {
    setSelectedSports((prev) => {
      if (prev.includes(sportId)) {
        return prev.filter((id) => id !== sportId);
      }
      // At the cap: ignore attempts to add a new sport (deselect still works).
      if (prev.length >= MAX_PROFILE_SPORTS) {
        return prev;
      }
      return [...prev, sportId];
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedSports.length === 0) {
      onNext(); // Skip if no sports selected
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      await profileApi.updateProfile({
        primary_sport: selectedSports[0],
        secondary_sports: selectedSports.slice(1),
      });
      onNext();
    } catch (err) {
      // #2698: do NOT advance-and-lose on failure. The old code caught, logged,
      // and called onNext() anyway — the user's selections were silently
      // discarded with no signal. Surface a blocking, retryable error (matches
      // ProfileBasicsStep's doctrine) and stay on the step so Continue retries.
      const message =
        err instanceof ApiError
          ? err.message
          : 'Failed to save your activities. Please try again.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Can proceed even without selection (optional step)
  const canProceed = true;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Progress Bar - Step 4 of 7 */}
      <div className="shrink-0 px-6 pt-4">
        <div className="w-full h-1 bg-oro-surface-alt rounded-full overflow-hidden">
          <div
            className="h-full bg-oro-gold rounded-full transition-all duration-300"
            style={{ width: '57%' }}
          />
        </div>
        <p className="text-text-muted text-xs mt-2 text-center">Step 4 of 7</p>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-6">
        {/* Icon Header (Nightfall pattern) */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-oro-gold to-oro-gold-dark flex items-center justify-center shadow-lg mb-4 mx-auto">
            <Heart className="w-8 h-8 text-black" />
          </div>
          <h2 id="onboarding-title" className="text-xl font-semibold text-text-primary mb-2">
            What moves you?
          </h2>
          <p className="text-text-muted text-sm">
            Select activities you enjoy. This helps us personalize your experience.
          </p>
        </div>

        {/* Sport Chips */}
        <form id="activities-form" onSubmit={handleSubmit}>
          <div className="flex flex-wrap gap-2">
            {SPORTS.map((sport) => {
              const isSelected = selectedSports.includes(sport.id);
              // At the cap, unselected chips are non-interactive (deselect still works).
              const isDisabled = !isSelected && atSelectionCap;
              return (
                <button
                  key={sport.id}
                  type="button"
                  onClick={() => toggleSport(sport.id)}
                  disabled={isDisabled}
                  aria-pressed={isSelected}
                  className={`px-4 py-2 rounded-full font-medium text-sm transition-all ${
                    isSelected
                      ? 'bg-oro-gold text-black shadow-md'
                      : isDisabled
                        ? 'bg-oro-surface-alt border border-oro-border text-text-muted opacity-50 cursor-not-allowed'
                        : 'bg-oro-surface-alt border border-oro-border text-text-secondary hover:border-oro-border-hover'
                  }`}
                >
                  {sport.label}
                </button>
              );
            })}
          </div>

          {/* Selection count / cap hint */}
          {selectedSports.length > 0 && (
            <p className="text-text-muted text-xs mt-4 text-center">
              {atSelectionCap
                ? `${selectedSports.length} selected (max ${MAX_PROFILE_SPORTS})`
                : `${selectedSports.length} selected`}
            </p>
          )}

          {/* Error Display (#2698 — blocking, retryable; does not advance) */}
          {error && (
            <div className="mt-4 p-4 bg-oro-error/10 border border-oro-error/20 rounded-xl" role="alert">
              <p className="text-sm text-oro-error">{error}</p>
            </div>
          )}
        </form>
      </div>

      {/* Bottom-Docked Actions */}
      <div className="shrink-0 p-4 border-t border-oro-border bg-oro-surface">
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onBack}
            className="px-4 py-3 rounded-lg bg-oro-surface-alt border border-oro-border text-text-primary hover:bg-oro-surface-alt/80 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="submit"
            form="activities-form"
            disabled={!canProceed || isSubmitting}
            className={`flex-1 px-4 py-3 rounded-lg flex items-center justify-center gap-2 transition-all ${
              canProceed && !isSubmitting
                ? 'bg-oro-gold hover:bg-oro-gold/90 shadow-lg'
                : 'bg-oro-surface-alt border border-oro-border text-text-muted cursor-not-allowed'
            }`}
          >
            {isSubmitting ? (
              <>
                <OroLoader variant="inline" color="currentColor" className="text-black" decorative />
                <span className="text-black font-medium">Saving...</span>
              </>
            ) : (
              <>
                <span className={canProceed ? 'text-black font-medium' : ''}>
                  {selectedSports.length > 0 ? 'Continue' : 'Skip'}
                </span>
                <ArrowRight className={`w-5 h-5 ${canProceed ? 'text-black' : ''}`} />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
